import { useState } from 'react';

export default function ProjectLifecycleSection() {
  const [selectedStage, setSelectedStage] = useState(0);

  const stages = [
    {
      title: 'Planning & Strategy',
      description: 'Define goals, audience insights, and content themes',
      percentage: 25,
      color: 'from-[#22577A] to-[#38a3a5]',
      icon: 'ri-compass-3-line',
      rotation: 0,
      tasks: [
        'Define campaign goals and KPIs',
        'Research target audience demographics',
        'Analyze competitor strategies',
        'Set content themes and messaging',
        'Establish brand voice guidelines',
      ],
    },
    {
      title: 'Content Creation',
      description: 'intelligent content generation and creative assets',
      percentage: 25,
      color: 'from-[#38a3a5] to-[#66b3ff]',
      icon: 'ri-ai-generate',
      rotation: 90,
      tasks: [
        'Generate AI-powered post captions',
        'Create visual assets and graphics',
        'Collect and organize media files',
        'Adapt content for each platform',
        'Review and approve drafts',
      ],
    },
    {
      title: 'Scheduling & Publishing',
      description: 'Optimize timing and automate multi-platform posting',
      percentage: 25,
      color: 'from-[#66b3ff] to-[#b3d9ff]',
      icon: 'ri-calendar-schedule-line',
      rotation: 180,
      tasks: [
        'Determine optimal posting times',
        'Queue content in calendar',
        'Set up multi-platform publishing',
        'Configure automation rules',
        'Schedule recurring content',
      ],
    },
    {
      title: 'Analytics & Optimization',
      description: 'Track performance and refine your strategy',
      percentage: 25,
      color: 'from-[#b3d9ff] to-[#e4f4ff]',
      icon: 'ri-line-chart-line',
      rotation: 270,
      tasks: [
        'Monitor real-time performance metrics',
        'Compare against competitors',
        'Analyze engagement patterns',
        'Generate performance reports',
        'Refine strategy based on insights',
      ],
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-white to-[#e4f4ff]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-refresh-line mr-2"></i>
            Your Project Lifecycle
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-6">
            From Vision to Victory
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            MandaPost guides you through every stage of your social media campaign, ensuring seamless execution from planning to performance analysis.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Donut Chart Visualization */}
          <div className="relative flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square">
              {/* Donut Ring */}
              <svg viewBox="0 0 200 200" className="w-full h-full transform -rotate-90">
                <defs>
                  <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#22577A" />
                    <stop offset="100%" stopColor="#38a3a5" />
                  </linearGradient>
                  <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#38a3a5" />
                    <stop offset="100%" stopColor="#66b3ff" />
                  </linearGradient>
                  <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#66b3ff" />
                    <stop offset="100%" stopColor="#b3d9ff" />
                  </linearGradient>
                  <linearGradient id="gradient4" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#b3d9ff" />
                    <stop offset="100%" stopColor="#e4f4ff" />
                  </linearGradient>
                </defs>

                {/* Background ring */}
                <circle
                  cx="100"
                  cy="100"
                  r="70"
                  fill="none"
                  stroke="#f0f0f0"
                  strokeWidth="30"
                />

                {/* Segment 1 - Planning */}
                <circle
                  cx="100"
                  cy="100"
                  r="70"
                  fill="none"
                  stroke="url(#gradient1)"
                  strokeWidth={selectedStage === 0 ? "35" : "30"}
                  strokeDasharray="109.96 439.82"
                  strokeDashoffset="0"
                  className="transition-all duration-300 cursor-pointer hover:opacity-80"
                  onClick={() => setSelectedStage(0)}
                />

                {/* Segment 2 - Content Creation */}
                <circle
                  cx="100"
                  cy="100"
                  r="70"
                  fill="none"
                  stroke="url(#gradient2)"
                  strokeWidth={selectedStage === 1 ? "35" : "30"}
                  strokeDasharray="109.96 439.82"
                  strokeDashoffset="-109.96"
                  className="transition-all duration-300 cursor-pointer hover:opacity-80"
                  onClick={() => setSelectedStage(1)}
                />

                {/* Segment 3 - Scheduling */}
                <circle
                  cx="100"
                  cy="100"
                  r="70"
                  fill="none"
                  stroke="url(#gradient3)"
                  strokeWidth={selectedStage === 2 ? "35" : "30"}
                  strokeDasharray="109.96 439.82"
                  strokeDashoffset="-219.91"
                  className="transition-all duration-300 cursor-pointer hover:opacity-80"
                  onClick={() => setSelectedStage(2)}
                />

                {/* Segment 4 - Analytics */}
                <circle
                  cx="100"
                  cy="100"
                  r="70"
                  fill="none"
                  stroke="url(#gradient4)"
                  strokeWidth={selectedStage === 3 ? "35" : "30"}
                  strokeDasharray="109.96 439.82"
                  strokeDashoffset="-329.87"
                  className="transition-all duration-300 cursor-pointer hover:opacity-80"
                  onClick={() => setSelectedStage(3)}
                />
              </svg>

              {/* Center Content - Dynamic */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center bg-white rounded-full w-36 h-36 flex flex-col items-center justify-center shadow-lg border-2 border-[#66b3ff]/30 relative z-10">
                  <div className={`w-12 h-12 bg-gradient-to-br ${stages[selectedStage].color} rounded-full flex items-center justify-center mb-2`}>
                    <i className={`${stages[selectedStage].icon} text-white text-xl`}></i>
                  </div>
                  <div className="text-sm font-bold text-[#22577A]">{stages[selectedStage].title.split(' ')[0]}</div>
                  <div className="text-xs text-gray-600 font-semibold mt-1">{stages[selectedStage].percentage}%</div>
                </div>
              </div>

              {/* Floating Labels */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-12">
                <div className="bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                  Planning
                </div>
              </div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12">
                <div className="bg-gradient-to-r from-[#38a3a5] to-[#66b3ff] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                  Creation
                </div>
              </div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-12">
                <div className="bg-gradient-to-r from-[#66b3ff] to-[#b3d9ff] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                  Scheduling
                </div>
              </div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12">
                <div className="bg-gradient-to-r from-[#b3d9ff] to-[#e4f4ff] text-[#22577A] px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                  Analytics
                </div>
              </div>
            </div>
          </div>

          {/* Stage Details - Interactive */}
          <div className="space-y-4">
            {stages.map((stage, index) => (
              <div key={index}>
                <button
                  onClick={() => setSelectedStage(index)}
                  className={`w-full bg-white rounded-xl border-2 p-6 hover:shadow-xl transition-all group cursor-pointer text-left ${
                    selectedStage === index ? 'border-[#66b3ff] shadow-lg' : 'border-gray-200 hover:border-[#66b3ff]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 bg-gradient-to-br ${stage.color} rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}>
                        <i className={`${stage.icon} text-white text-2xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#22577A] mb-1">{stage.title}</h3>
                        <p className="text-gray-600 text-sm">{stage.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 bg-gradient-to-r ${stage.color} text-white rounded-full text-sm font-semibold`}>
                        {stage.percentage}%
                      </span>
                      <i className={`ri-arrow-${selectedStage === index ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
                    </div>
                  </div>
                </button>

                {/* Expandable Task List */}
                {selectedStage === index && (
                  <div className="mt-3 bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10 rounded-xl border-2 border-[#66b3ff]/30 p-6 animate-fadeIn">
                    <h4 className="font-semibold text-[#22577A] mb-4 flex items-center gap-2">
                      <i className="ri-task-line text-[#66b3ff]"></i>
                      Key Tasks in This Stage:
                    </h4>
                    <ul className="space-y-3">
                      {stage.tasks.map((task, taskIndex) => (
                        <li key={taskIndex} className="flex items-start gap-3">
                          <div className={`w-6 h-6 bg-gradient-to-br ${stage.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <span className="text-gray-700 leading-relaxed">{task}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="grid md:grid-cols-4 gap-6 mt-16">
          <div className="bg-gradient-to-br from-[#22577A]/10 to-[#38a3a5]/10 rounded-xl p-6 text-center border-2 border-[#22577A]/20">
            <div className="text-3xl font-bold text-[#22577A] mb-2">4</div>
            <div className="text-sm text-gray-700 font-medium">Integrated Stages</div>
          </div>
          <div className="bg-gradient-to-br from-[#38a3a5]/10 to-[#66b3ff]/10 rounded-xl p-6 text-center border-2 border-[#38a3a5]/20">
            <div className="text-3xl font-bold text-[#22577A] mb-2">100%</div>
            <div className="text-sm text-gray-700 font-medium">Workflow Coverage</div>
          </div>
          <div className="bg-gradient-to-br from-[#66b3ff]/10 to-[#b3d9ff]/10 rounded-xl p-6 text-center border-2 border-[#66b3ff]/20">
            <div className="text-3xl font-bold text-[#22577A] mb-2">Real-time</div>
            <div className="text-sm text-gray-700 font-medium">Sync & Updates</div>
          </div>
          <div className="bg-gradient-to-br from-[#b3d9ff]/10 to-[#e4f4ff]/10 rounded-xl p-6 text-center border-2 border-[#b3d9ff]/20">
            <div className="text-3xl font-bold text-[#22577A] mb-2">AI-Powered</div>
            <div className="text-sm text-gray-700 font-medium">Every Step</div>
          </div>
        </div>
      </div>
    </section>
  );
}
